/**
 * ThreadSearchBox — ONE search box over the user's threads (chat-thread
 * experience PR-5, 2026-09-09), mounted in the rail header and the mobile
 * sheet. Two tiers, one box:
 *   • INSTANT — every keystroke runs the pure corpus search over what the
 *     browser holds (thread names incl. former names, messages in local
 *     storage / state) through `api.instant`;
 *   • DEEP — Enter, or the "search all messages" row, asks the server
 *     (`api.deep`, the `threadSearch` function) for hits across the caller's
 *     own threads; results replace the instant list, a failure says so.
 * A result opens its thread and, for a message, scrolls to the row
 * (`api.onOpen`). Escape clears. Chrome copy only (D9).
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import type { ThreadSearchHit, ThreadSearchResults } from '../../services/threadSearch/searchThreadCorpus';

export interface ThreadSearchApi {
  instant: (query: string) => ThreadSearchResults;
  /** null = the deep tier failed (offline, refused). Absent = no deep tier. */
  deep?: (query: string) => Promise<ThreadSearchHit[] | null>;
  onOpen: (hit: ThreadSearchHit) => void;
}

export interface ThreadSearchBoxProps {
  api: ThreadSearchApi;
  variant?: 'rail' | 'sheet';
  placeholder?: string;
  /** Called when a result opens (the sheet closes itself). */
  onOpened?: () => void;
}

const SearchWrap = styled.div`
  padding: 0 12px 6px 12px;
`;

const SearchInputRow = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 7px 28px 7px 30px;
  border: 1px solid rgba(198, 167, 94, 0.28);
  border-radius: 8px;
  background: rgba(198, 167, 94, 0.06);
  color: rgba(255, 255, 255, 0.92);
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 12.5px;
  outline: none;

  &::placeholder {
    color: rgba(182, 146, 76, 0.7);
  }

  &:focus {
    border-color: rgba(198, 167, 94, 0.6);
    background: rgba(198, 167, 94, 0.09);
  }
`;

const SearchGlyph = styled.svg`
  position: absolute;
  left: 9px;
  width: 13px;
  height: 13px;
  pointer-events: none;
`;

const SearchClearButton = styled.button`
  position: absolute;
  right: 6px;
  width: 20px;
  height: 20px;
  padding: 0;
  border: none;
  border-radius: 50%;
  background: transparent;
  color: rgba(182, 146, 76, 0.85);
  font-size: 14px;
  line-height: 1;
  cursor: pointer;

  &:hover {
    color: var(--brand-gold);
    background: rgba(198, 167, 94, 0.12);
  }
`;

const ResultsPanel = styled.div`
  margin-top: 6px;
  max-height: min(48vh, 420px);
  overflow-y: auto;
  border: 1px solid rgba(198, 167, 94, 0.22);
  border-radius: 8px;
  background: rgba(0, 0, 0, 0.18);
  scrollbar-width: thin;
  scrollbar-color: rgba(198, 167, 94, 0.25) transparent;
`;

const ResultsHeading = styled.div`
  padding: 7px 10px 3px;
  color: rgba(182, 146, 76, 0.85);
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 0.68rem;
  font-weight: 600;
  letter-spacing: 0.02em;
`;

const ResultRow = styled.button`
  display: block;
  width: 100%;
  padding: 7px 10px;
  border: none;
  border-radius: 0;
  background: transparent;
  color: rgba(255, 255, 255, 0.9);
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  text-align: left;
  cursor: pointer;
  appearance: none;
  -webkit-appearance: none;

  &:hover,
  &:focus-visible {
    background: rgba(198, 167, 94, 0.1);
    outline: none;
  }
`;

const ResultTitle = styled.div`
  font-size: 12.5px;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ResultSnippet = styled.div`
  margin-top: 2px;
  color: rgba(255, 255, 255, 0.62);
  font-size: 11.5px;
  line-height: 1.35;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const ResultNote = styled.div`
  padding: 8px 10px 9px;
  color: rgba(182, 146, 76, 0.8);
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 11.5px;
`;

const INSTANT_DEBOUNCE_MS = 80;

type DeepState = 'idle' | 'loading' | 'done' | 'failed';

export const ThreadSearchBox: React.FC<ThreadSearchBoxProps> = ({ api, variant = 'rail', placeholder, onOpened }) => {
  const [query, setQuery] = useState('');
  const [instant, setInstant] = useState<ThreadSearchResults>({ threads: [], messages: [] });
  const [deepState, setDeepState] = useState<DeepState>('idle');
  const [deepHits, setDeepHits] = useState<ThreadSearchHit[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);
  const deepRunRef = useRef(0);

  const trimmed = query.trim();

  // Instant tier, debounced a beat so a fast typist never pays per key.
  useEffect(() => {
    if (!trimmed) {
      setInstant({ threads: [], messages: [] });
      setDeepState('idle');
      setDeepHits([]);
      return undefined;
    }
    const timer = window.setTimeout(() => {
      try {
        setInstant(api.instant(trimmed));
      } catch {
        setInstant({ threads: [], messages: [] });
      }
    }, INSTANT_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [trimmed, api]);

  // A new query retires any deep result of the previous one.
  useEffect(() => {
    setDeepState('idle');
    setDeepHits([]);
    deepRunRef.current += 1;
  }, [trimmed]);

  const runDeep = useCallback(async () => {
    if (!api.deep || trimmed.length < 2) return;
    const run = deepRunRef.current + 1;
    deepRunRef.current = run;
    setDeepState('loading');
    const hits = await api.deep(trimmed);
    if (deepRunRef.current !== run) return; // superseded
    if (hits === null) {
      setDeepState('failed');
      setDeepHits([]);
      return;
    }
    setDeepHits(hits);
    setDeepState('done');
  }, [api, trimmed]);

  const clear = useCallback(() => {
    setQuery('');
    inputRef.current?.focus();
  }, []);

  const open = useCallback((hit: ThreadSearchHit) => {
    api.onOpen(hit);
    setQuery('');
    onOpened?.();
  }, [api, onOpened]);

  const showPanel = trimmed.length > 0;
  const deepMessageHits = useMemo(() => deepHits.filter((h) => h.kind === 'message'), [deepHits]);
  const deepThreadHits = useMemo(() => deepHits.filter((h) => h.kind !== 'message'), [deepHits]);
  const threadHits = deepState === 'done' && deepThreadHits.length > 0 ? deepThreadHits : instant.threads;
  const messageHits = deepState === 'done' ? deepMessageHits : instant.messages;
  const nothing = threadHits.length === 0 && messageHits.length === 0;

  return (
    <SearchWrap data-thread-search={variant}>
      <SearchInputRow>
        <SearchGlyph viewBox="0 0 24 24" fill="none" stroke="rgba(182, 146, 76, 0.85)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <circle cx="11" cy="11" r="7" />
          <path d="M21 21l-4.3-4.3" />
        </SearchGlyph>
        <SearchInput
          ref={inputRef}
          type="search"
          role="searchbox"
          aria-label="search threads and messages"
          data-thread-search-input=""
          placeholder={placeholder ?? 'search threads…'}
          value={query}
          autoComplete="off"
          spellCheck={false}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              if (query) {
                e.preventDefault();
                e.stopPropagation();
                clear();
              }
              return;
            }
            if (e.key === 'Enter') {
              e.preventDefault();
              void runDeep();
            }
          }}
        />
        {query && (
          <SearchClearButton type="button" aria-label="clear search" data-thread-search-clear="" onClick={clear}>
            ×
          </SearchClearButton>
        )}
      </SearchInputRow>
      {showPanel && (
        <ResultsPanel data-thread-search-results="" role="listbox" aria-label="search results">
          {threadHits.length > 0 && (
            <>
              <ResultsHeading>threads</ResultsHeading>
              {threadHits.map((hit) => (
                <ResultRow
                  key={`t:${hit.threadId}:${hit.kind}`}
                  type="button"
                  role="option"
                  aria-selected={false}
                  data-thread-search-hit={hit.kind}
                  data-thread-search-thread-id={hit.threadId}
                  onClick={() => open(hit)}
                >
                  <ResultTitle>{hit.title || hit.snippet}</ResultTitle>
                  {hit.kind === 'former-title' && (
                    <ResultSnippet>was “{hit.snippet}”</ResultSnippet>
                  )}
                </ResultRow>
              ))}
            </>
          )}
          {messageHits.length > 0 && (
            <>
              <ResultsHeading>{deepState === 'done' ? 'messages · everywhere' : 'messages'}</ResultsHeading>
              {messageHits.map((hit) => (
                <ResultRow
                  key={`m:${hit.threadId}:${hit.messageId ?? hit.sharedMessageId ?? hit.snippet}`}
                  type="button"
                  role="option"
                  aria-selected={false}
                  data-thread-search-hit="message"
                  data-thread-search-thread-id={hit.threadId}
                  onClick={() => open(hit)}
                >
                  <ResultTitle>{hit.title || 'thread'}</ResultTitle>
                  <ResultSnippet>{hit.snippet}</ResultSnippet>
                </ResultRow>
              ))}
            </>
          )}
          {nothing && deepState !== 'loading' && (
            <ResultNote data-thread-search-empty="">
              {deepState === 'done' ? 'nothing matches — anywhere.' : 'nothing here matches yet.'}
            </ResultNote>
          )}
          {api.deep && deepState === 'idle' && trimmed.length >= 2 && (
            <ResultRow type="button" data-thread-search-deep="" onClick={() => void runDeep()}>
              <ResultTitle>search all messages ↵</ResultTitle>
            </ResultRow>
          )}
          {deepState === 'loading' && <ResultNote data-thread-search-loading="">searching all messages…</ResultNote>}
          {deepState === 'failed' && (
            <ResultNote data-thread-search-failed="">couldn’t search everything — check your connection and try again.</ResultNote>
          )}
        </ResultsPanel>
      )}
    </SearchWrap>
  );
};

export default ThreadSearchBox;
